import axios from 'axios'
import { APIClient } from './apiClient'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || (
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.hostname.replace('ai.im', 'api.im').replace('almaysarah-dashboard', 'almaysarah-backend')}/api`
    : 'http://localhost:3000/api'
)
const USE_MOCK_DATA = import.meta.env.VITE_USE_MOCK_DATA === 'true'

export interface LoginCredentials {
  username: string
  password: string
  subdomain?: string
}

export interface User {
  id: string
  username?: string
  email: string
  firstName: string
  lastName: string
  role: string
  departmentId?: string
  tenantId: string
  permissions: string[]
}

export interface AuthResponse {
  success: boolean
  data?: {
    user?: User
    token?: string
    refreshToken?: string
    tenants?: Array<{
      tenantId: string
      name: string
      subdomain: string
      role: string
      departmentId?: string
    }>
    tempToken?: string
  }
  error?: string
}

class AuthService {
  private token: string | null = null
  private refreshToken: string | null = null
  private user: User | null = null

  constructor() {
    // Load stored auth data on initialization
    this.loadStoredAuth()
  }

  private async fetchUserInfo(token: string) {
    try {
      // Use APIClient to benefit from interceptors and base configuration
      // We pass the token manually for this specific initial call to ensure it's used
      const response = await APIClient.getInstance().get('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data && response.data.success) {
        this.user = response.data.data
        localStorage.setItem('user', JSON.stringify(this.user))
        return this.user
      }
    } catch (error) {
      console.error('Error fetching user info:', error)
    }
    return null
  }

  private loadStoredAuth() {
    this.token = localStorage.getItem('authToken')
    this.refreshToken = localStorage.getItem('refreshToken')
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        this.user = JSON.parse(storedUser)
      } catch (error) {
        console.error('Error parsing stored user data:', error)
        this.clearStoredAuth()
      }
    }
  }

  private storeAuth(token: string, refreshToken: string, user: User) {
    this.token = token
    this.refreshToken = refreshToken
    this.user = user

    localStorage.setItem('authToken', token)
    localStorage.setItem('refreshToken', refreshToken)
    localStorage.setItem('user', JSON.stringify(user))
  }

  private clearStoredAuth() {
    this.token = null
    this.refreshToken = null
    this.user = null

    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    if (USE_MOCK_DATA) {
      // ... (mock logic kept for testing)
      await new Promise(resolve => setTimeout(resolve, 1000))
      const mockUser: User = {
        id: 'mock-user-1',
        username: credentials.username,
        email: `${credentials.username}@mock.com`,
        firstName: 'John',
        lastName: 'Doe',
        role: 'admin',
        tenantId: 'tenant-1',
        permissions: ['read', 'write', 'admin']
      }
      const mockToken = 'mock-jwt-token'
      this.storeAuth(mockToken, '', mockUser)
      return { success: true, data: { user: mockUser, token: mockToken, refreshToken: '' } }
    }

    try {
      // Step 1: Login to get possible tenants
      const response = await axios.post(`${API_BASE_URL}/auth/login-multi`, {
        username: credentials.username,
        password: credentials.password
      })

      if (response.data.success && response.data.data) {
        const data = response.data.data

        // Case A: Single tenant found, logged in already
        if (data.user && data.accessToken) {
          const { user, accessToken, refreshToken } = data
          this.storeAuth(accessToken, refreshToken, user)
          return {
            success: true,
            data: {
              user,
              token: accessToken,
              refreshToken
            }
          }
        }

        // Case B: Multiple tenants found, need selection
        return {
          success: true,
          data: {
            tenants: data.tenants,
            tempToken: data.tempToken
          }
        }
      } else {
        return {
          success: false,
          error: response.data.error || 'Login failed'
        }
      }
    } catch (error: any) {
      console.error('Login error:', error)
      let errorMessage = 'Login failed. Please try again.'

      if (error.response?.status === 401) {
        errorMessage = 'Invalid username or password.'
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many failed login attempts. Please try again later.'
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }

      return {
        success: false,
        error: errorMessage
      }
    }
  }

  async selectTenant(tempToken: string, tenantId: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/select-tenant`, {
        tempToken,
        tenantId
      })

      if (response.data.success && response.data.data) {
        const { user, accessToken, refreshToken } = response.data.data
        this.storeAuth(accessToken, refreshToken, user)

        return {
          success: true,
          data: {
            user,
            token: accessToken,
            refreshToken
          }
        }
      } else {
        return {
          success: false,
          error: response.data.error || 'Tenant selection failed'
        }
      }
    } catch (error: any) {
      console.error('Select tenant error:', error)
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to select tenant'
      }
    }
  }

  async logout(): Promise<void> {
    try {
      // Call backend logout endpoint if needed
      if (this.token) {
        await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        }).catch(() => {
          // Ignore logout errors, still clear local state
        })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      this.clearStoredAuth()
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      return false
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refreshToken: this.refreshToken
      })

      if (response.data.success) {
        const { accessToken, refreshToken } = response.data.data

        // Update tokens (preserve existing user since refresh doesn't return user data)
        this.token = accessToken
        this.refreshToken = refreshToken
        localStorage.setItem('authToken', accessToken)
        localStorage.setItem('refreshToken', refreshToken)

        // Re-fetch user info to ensure it's up to date
        await this.fetchUserInfo(accessToken)

        return true
      } else {
        this.clearStoredAuth()
        return false
      }
    } catch (error) {
      console.error('Token refresh error:', error)
      this.clearStoredAuth()
      return false
    }
  }

  getToken(): string | null {
    return this.token
  }

  getUser(): User | null {
    return this.user
  }

  isAuthenticated(): boolean {
    return !!this.token && !!this.user
  }

  hasRole(role: string): boolean {
    return this.user?.role === role
  }

  hasPermission(permission: string): boolean {
    return this.user?.permissions?.includes(permission) || false
  }

  getTenantId(): string | null {
    return this.user?.tenantId || null
  }
}

export const authService = new AuthService()
export default authService